import { LitElement, css, html } from 'lit'
import { property, customElement, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import {
    AlertType,
    VesselAlert,
    VesselAlertsModel,
} from '../../../src/models/vessel-alerts-model'
import './voy-alert-message'

import {
    AlertConfig,
    getAlertConfig,
    getPriorityOrder,
} from '../utils/alert-configuration'

interface SortedAlert extends VesselAlert {
    priority: number
    config: AlertConfig
}

@customElement('voy-alert-messages')
export class VoyAlertMessages extends LitElement {
    @property({ type: Number })
    get imo(): number {
        return this._imo
    }

    set imo(value: number) {
        const oldValue = this._imo
        this._imo = value
        this.requestUpdate('imo', oldValue)
        this._imoChanged()
    }

    @state() private _showAll = false

    constructor() {
        super()
        this._onAlertsChanged = this._onAlertsChanged.bind(this)
    }

    static get styles() {
        return [
            css`
                :host {
                    position: relative;
                    display: block;
                    /* The host is stretched to the full flex-row width by main-app's
                       ".alertsContainer > *" rule, but the visible banner below is only
                       as wide as its content (fit-content). Without this, the empty
                       space to the right of the banner still swallows mousedown/drag
                       and blocks map pan whenever an alert is showing. */
                    pointer-events: none;
                }

                .alerts {
                    position: relative;
                    display: flex;
                    flex-direction: row;
                    padding: 8px;
                    gap: 16px;
                    justify-content: space-between;
                    border-radius: 8px;
                    max-width: 1200px;
                    width: fit-content;
                    /* Re-enable interaction within the actual visible banner area; see
                       :host for why pointer-events is disabled by default. */
                    pointer-events: auto;
                }

                .alerts--panel {
                    background-color: rgba(237, 237, 237, 0.5);
                }

                .alerts-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .alerts-list .alert-item {
                    pointer-events: auto;
                    width: 100%;
                }

                .view-toggle-button {
                    margin-top: 6px;
                    align-self: flex-start;
                    white-space: nowrap;
                }
            `,
        ]
    }

    render() {
        const sortedAlerts = this._getSortedAlerts()
        const displayedAlerts = this._showAll
            ? sortedAlerts
            : sortedAlerts.slice(0, 3)
        const hasMoreAlerts = sortedAlerts.length > 3

        return html`
            <div
                class="alerts ${sortedAlerts.length > 1 ? 'alerts--panel' : ''}"
            >
                <div
                    class="alerts-list ${this._showAll ? 'expanded' : ''}"
                >
                    ${repeat(
                        displayedAlerts,
                        (alert) => alert.id, // Use alert ID as unique key
                        ({ config, priority: _, ...alert }, index) => {
                            return html`
                                <div
                                    class="alert-item"
                                    data-index="${index}"
                                    data-alert-id="${alert.id}"
                                >
                                    <voy-alert-message
                                        .config=${config}
                                        .alert=${alert}
                                        .expandByDefault=${sortedAlerts.length ===
                                        1 ||
                                        alert.type ===
                                        AlertType.ShallowSoundingsWarning}
                                        .toggleable=${sortedAlerts.length !==
                                        1 ||
                                        alert.type ===
                                        AlertType.ShallowSoundingsWarning}
                                        @close=${() =>
                                            this._closeAlert(alert.id)}
                                        @alert-expanded=${(e: CustomEvent) =>
                                            this._onAlertExpanded(
                                                e.detail.alertId,
                                                e.detail.expanded,
                                            )}
                                    ></voy-alert-message>
                                </div>
                            `
                        },
                    )}
                </div>
                ${hasMoreAlerts
                    ? html`
                          <mc-button
                              class="view-toggle-button"
                              appearance="neutral"
                              @click=${this._toggleViewAll}
                              fit="small"
                          >
                              ${this._showAll ? 'View Less' : 'View All'}
                          </mc-button>
                      `
                    : ''}
            </div>
        `
    }

    connectedCallback() {
        super.connectedCallback()
        if (this.imo) {
            this._alerts = VesselAlertsModel!.getAlerts(this.imo)
        }
        VesselAlertsModel!.addEventListener(
            'alerts-changed',
            <EventListener>this._onAlertsChanged,
        )
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        VesselAlertsModel!.removeEventListener(
            'alerts-changed',
            <EventListener>this._onAlertsChanged,
        )
    }

    @property({ attribute: false }) private _alerts: VesselAlert[] = []
    private _imo: number = 0

    private _getSortedAlerts(): SortedAlert[] {
        return this._alerts
            .map((alert) => {
                const config = getAlertConfig(alert)
                return {
                    ...alert,
                    priority: getPriorityOrder({
                        status: config?.appearance,
                        closable: config?.closable,
                    }),
                    config: config!,
                } as SortedAlert
            })
            .sort((a, b) => {
                // order by priority, then by timestamps
                if (a.priority !== b.priority) {
                    return a.priority - b.priority
                }

                const timeA = a.time ? new Date(a.time).getTime() : 0
                const timeB = b.time ? new Date(b.time).getTime() : 0
                return timeB - timeA
            })
    }

    private _onAlertsChanged(
        evt: CustomEvent<{ imo: number; alerts: VesselAlert[] }>,
    ) {
        if (this._imo === evt.detail.imo) {
            this._alerts = evt.detail.alerts
        }
        this.requestUpdate()
    }

    private _imoChanged() {
        if (this._imo) {
            this._alerts = VesselAlertsModel!.getAlerts(this._imo)
        } else {
            this._alerts = []
        }
    }

    private _closeAlert(alertId: string) {
        // clearing expanded state (of closed alert) before removing the alert to prevent DOM reuse & layout issues
        const alertWrapper = this.shadowRoot?.querySelector(
            `[data-alert-id="${alertId}"]`,
        )
        if (alertWrapper) {
            alertWrapper.classList.remove('expanded')
        }
        VesselAlertsModel!.removeAlert(this._imo, alertId)
    }

    private _toggleViewAll() {
        this._showAll = !this._showAll
    }

    private _onAlertExpanded(alertId: string, expanded: boolean) {
        const alertWrapper = this.shadowRoot?.querySelector(
            `[data-alert-id="${alertId}"]`,
        )
        if (alertWrapper) {
            if (expanded) {
                alertWrapper.classList.add('expanded')
            } else {
                alertWrapper.classList.remove('expanded')
            }
        }
    }
}
